import * as Tooltip from '@radix-ui/react-tooltip';
import { mergeRefs } from '@react-aria/utils';
import { useIsOverflowing } from '@vizit/hooks';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { textClasses } from './styles';
import { type FontFamily, TextColor, TextEllipsis, type TextSize, type TextVariant, type TextWeight } from './types';

interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  /**
   * The color variant of the text.
   *
   * @optional
   * @type 'primary' | 'secondary' | 'danger'
   * @default 'primary'
   */
  color?: TextColor;
  /**
   * The element to render as the node.
   *
   * @optional
   * @type ElementType
   * @default 'div'
   */
  elementType?: React.ElementType;
  /**
   * The font family for the text.
   *
   * @optional
   * @type 'sans' | 'serif' | 'mono'
   * @default 'sans'
   */
  fontFamily?: FontFamily;
  /**
   * The maximum number of lines to display before truncating.
   *
   * @optional
   * @type number
   * @default 1
   */
  maxLines?: number;
  /**
   * Whether to show the ellipsis and when the text overflows (shows a tooltip with the full text on hover).
   *
   * @optional
   * @type boolean
   * @default true
   */
  showEllipsis?: boolean;
  /**
   * The size of the text.
   *
   * @optional
   * @type 'xs' | 'sm' | 'md' | 'lg' | 'xl'
   * @default 'lg'
   */
  size?: TextSize;
  /**
   * Semantic text variant that combines font-family, size, and weight.
   * When specified, this takes precedence over individual size, weight, and fontFamily props.
   *
   * @optional
   * @type TextVariant
   */
  variant?: TextVariant;
  /**
   * The weight of the text.
   *
   * @optional
   * @type 'bold' | 'semibold' | 'medium' | 'regular'
   * @default 'regular'
   */
  weight?: TextWeight;
}

/**
 * The Text component is used to display text.
 *
 * @param props - The props for the Text component.
 * @example
 * <Text color={TextColor.Primary} size={TextSize.Md} weight={TextWeight.Bold}>
 *   Text
 * </Text>
 */
const Text = React.forwardRef<HTMLDivElement, TextProps>((props, forwardedRef) => {
  const {
    className,
    children,
    color = TextColor.Primary,
    elementType: Component = 'div',
    fontFamily,
    maxLines = 1,
    showEllipsis = true,
    size,
    variant,
    weight,
    ...other
  } = props;

  const triggerRef = React.useRef<HTMLDivElement | null>(null);

  const textRef = (node: HTMLDivElement | null) => {
    if (node) {
      node.style.setProperty('--text-line-clamp', maxLines.toString());

      triggerRef.current = node;
    }
  };

  const mergedRefs = mergeRefs<HTMLDivElement>(forwardedRef, textRef);

  const isOverflowing = useIsOverflowing(triggerRef, maxLines);

  const showTooltip = showEllipsis && isOverflowing && typeof children === 'string';

  let ellipsis: TextEllipsis = TextEllipsis.None;

  if (showEllipsis) {
    ellipsis = maxLines > 1 ? TextEllipsis.Multiple : TextEllipsis.Single;
  }

  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <Component
            {...other}
            ref={mergedRefs}
            className={cx(
              textClasses({
                color,
                ellipsis,
                // When variant is specified, it provides font-family, size, and weight
                // so we don't pass individual props to avoid conflicts
                fontFamily: variant ? undefined : fontFamily,
                size: variant ? undefined : size,
                weight: variant ? undefined : weight,
                variant,
              }),
              className,
            )}
          >
            {children}
          </Component>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          {showTooltip && (
            <Tooltip.Content
              className={cx(
                'z-50 max-w-sm rounded bg-gray-dark-975 p-4 text-xs text-white',
                'flex items-center justify-center',

                /* Enter animation */
                'animate-in fade-in-0 zoom-in-95',

                /* Exit animation */
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',

                /* Placement animations */
                'data-[side=bottom]:slide-in-from-top-2',
                'data-[side=left]:slide-in-from-right-2',
                'data-[side=right]:slide-in-from-left-2',
                'data-[side=top]:slide-in-from-bottom-2',
              )}
            >
              {children}
              <Tooltip.Arrow className="fill-gray-dark-975 stroke-gray-dark-975" />
            </Tooltip.Content>
          )}
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
});

export { Text, type TextProps };
